import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useI18n } from '../../i18n';
import fr from '../../i18n/locales/fr';

const API_URL = process.env.REACT_APP_API_URL || '';

const VerifyEmail = () => {
  const { t: originalT } = useI18n();

  // Use French translations for the verification page (French platform consistency)
  // Memoized to avoid recreation on every render (used in useEffect dependencies)
  const t = React.useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = fr;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value !== 'string') return key;
    return value.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? String(params[param]) : match;
    });
  }, []);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error, no-token
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '', sending, sent, error
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        await axios.get(`${API_URL}/api/v1/auth/verify-email/${token}`);
        setStatus('success');
      } catch (err) {
        setStatus('error');
        if (err.response?.data?.detail) {
          setErrorMessage(err.response.data.detail);
        } else if (!err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network'))) {
          // Network error (ERR_NETWORK_CHANGED, offline, etc.)
          setErrorMessage(t('errors.networkError'));
        } else {
          setErrorMessage(t('errors.verificationFailed'));
        }
      }
    };

    verifyEmail();
  }, [token, t]);

  // Countdown and redirect after success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/pricing?welcome=true');
    }
  }, [status, countdown, navigate]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;

    setResendStatus('sending');
    try {
      await axios.post(`${API_URL}/api/v1/auth/resend-verification`, {
        email: resendEmail
      });
      setResendStatus('sent');
    } catch (err) {
      if (err.response?.status === 429) {
        setResendStatus('error');
        setErrorMessage(t('errors.rateLimitExceeded'));
      } else if (!err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network'))) {
        // Network error - show error to user since this is not about email existence
        setResendStatus('error');
        setErrorMessage(t('errors.networkError'));
      } else {
        setResendStatus('sent'); // Don't reveal if email exists
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,136,0.3) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,136,0.2) 0%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Branding header */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="SYLINK"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">SyLink</h1>
          <p className="text-sm text-gray-400 mt-1">Pentest Platform</p>
        </div>

        <div
          className="bg-[#0d0d0d]/90 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 text-center"
          style={{ boxShadow: '0 0 50px rgba(0, 255, 136, 0.1)' }}
        >
          {/* Verifying */}
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-brand-green animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('auth.verifying')}</h2>
              <p className="text-gray-400">
                {t('auth.verifyingDescription')}
              </p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center animate-pulse">
                <CheckCircle className="w-10 h-10 text-brand-green" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('auth.emailVerified')}</h2>
              <p className="text-gray-400 mb-6">
                {t('auth.accountActive')}
              </p>

              {/* Success animation */}
              <div className="bg-gradient-to-r from-brand-green/10 to-emerald-500/10 rounded-xl p-6 mb-6 border border-brand-green/20">
                <Sparkles className="w-8 h-8 text-brand-green mx-auto mb-3" />
                <p className="text-white font-medium mb-2">{t('auth.welcome')}</p>
                <p className="text-gray-400 text-sm">
                  {t('auth.redirectingToPlan', { seconds: countdown })}
                </p>
              </div>

              <button
                onClick={() => navigate('/pricing?welcome=true')}
                className="inline-flex items-center justify-center gap-2 w-full bg-brand-green hover:bg-brand-green/90 text-black font-semibold py-3 rounded-xl transition-colors"
              >
                {t('auth.choosePlan')}
                <ArrowRight className="w-5 h-5" />
              </button>

              <p className="text-gray-500 text-sm mt-4">
                Ou <Link to="/login" className="text-brand-green hover:underline">{t('auth.login')}</Link> directement
              </p>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('auth.verificationFailed')}</h2>
              <p className="text-gray-400 mb-6">{errorMessage}</p>

              {/* Resend form */}
              <div className="bg-[#111111] border border-gray-800 rounded-xl p-4 mt-6">
                <p className="text-sm text-gray-400 mb-4">
                  {t('auth.needNewVerificationLink')}
                </p>
                <form onSubmit={handleResendVerification} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-brand-green/50"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                    className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus === 'sending' ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('common.sending')}
                      </span>
                    ) : resendStatus === 'sent' ? (
                      t('auth.emailSent')
                    ) : (
                      t('auth.resendLink')
                    )}
                  </button>
                </form>
              </div>

              <Link
                to="/login"
                className="inline-block mt-6 text-brand-green hover:text-brand-green/80 text-sm transition-colors"
              >
                {t('auth.backToLogin')}
              </Link>
            </>
          )}

          {/* No token */}
          {status === 'no-token' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/10 flex items-center justify-center">
                <Mail className="w-10 h-10 text-brand-green" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">{t('auth.invalidLink')}</h2>
              <p className="text-gray-400 mb-6">
                {t('errors.invalidVerificationLink')}
              </p>

              {/* Resend form */}
              <div className="bg-[#111111] border border-gray-800 rounded-xl p-4">
                <p className="text-sm text-gray-400 mb-4">
                  {t('auth.requestNewVerificationLink')}
                </p>
                <form onSubmit={handleResendVerification} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder={t('auth.emailPlaceholder')}
                      className="w-full bg-[#0a0a0a] border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-brand-green/50"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                    className="w-full bg-brand-green hover:bg-brand-green/90 text-black font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendStatus === 'sending' ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('common.sending')}
                      </span>
                    ) : resendStatus === 'sent' ? (
                      t('auth.emailSent')
                    ) : (
                      t('auth.sendLink')
                    )}
                  </button>
                </form>
              </div>

              <Link
                to="/login"
                className="inline-block mt-6 text-brand-green hover:text-brand-green/80 text-sm transition-colors"
              >
                {t('auth.backToLogin')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
